import type {
  OperationDefinitionNode,
  DocumentNode,
  FieldNode,
  ArgumentNode,
} from 'graphql';
import { RestResourceResolverArgs } from '../types';

/**
 * fullDocumentToFieldDocument trims down our query to just the fields that are relevant.
 * For example, if we have a query that wants member and organization, at the point
 * it hits the restResourceResolver we only get the field for member or organization.
 * So we can trim down context.document based on the field.node.name by filtering the
 * document selections.
 *
 * We also do some tricky things in here like convert the arguments and variables for the document
 * into hard coded arguments. So for example
 * query SomeQuery($myId: ID!) {
 *  member(id: $myId) {
 *  ...
 *  }
 * }
 * gets converted to
 * query SomeQuery {
 *  member(id: "myId") -> where "myId" is the value of myId passed into the query
 * }
 *
 * We do this because doing client.readQuery requires variables that match the document. When
 * apollo hits the resolver, its already mapped the variables from the initial variable names
 * to the argument name based on the schema. So myId -> id.
 */
export const fullDocumentToFieldDocument = (
  document: DocumentNode,
  field: FieldNode,
  rootArgs: RestResourceResolverArgs,
): DocumentNode => {
  const formatArgument = (argument: ArgumentNode): ArgumentNode => {
    if (argument.value.kind === 'Variable') {
      if (Array.isArray(rootArgs[argument.name.value])) {
        return {
          ...argument,
          value: {
            kind: 'ListValue',
            values: (rootArgs[argument.name.value] as string[]).map(
              (value) => ({
                kind: 'StringValue',
                value,
                block: false,
              }),
            ),
          },
        };
      }

      // this doesn't really make a difference compared to StringValue but doing so for consistency
      if (Number.isInteger(rootArgs[argument.name.value])) {
        return {
          ...argument,
          value: {
            kind: 'IntValue',
            value: rootArgs[argument.name.value] as string,
          },
        };
      }

      return {
        ...argument,
        value: {
          kind: 'StringValue',
          value: rootArgs[argument.name.value] as string,
          block: false,
        },
      };
    }

    return argument;
  };

  const formatSelection = (selection: FieldNode): FieldNode => {
    const result = {
      ...selection,
      arguments: selection.arguments?.map(formatArgument),
    };

    if (selection.selectionSet) {
      result.selectionSet = {
        ...selection.selectionSet,
        selections: (selection.selectionSet.selections.filter(
          (selection) => selection.kind === 'Field',
        ) as FieldNode[]).map(formatSelection),
      };
    }

    return result;
  };

  const formatDefinition = (
    definition: OperationDefinitionNode,
  ): OperationDefinitionNode => {
    return {
      ...definition,
      // empty out variable definitions in favor of using literal values
      variableDefinitions: [],
      selectionSet: {
        ...definition.selectionSet,
        selections: (definition.selectionSet.selections.filter(
          ({ kind }) => kind === 'Field',
        ) as FieldNode[])
          .filter((selection) => selection.name.value === field.name.value)
          .map(formatSelection),
      },
    };
  };

  return {
    ...document,
    definitions: (document.definitions.filter(
      (definition) => definition.kind === 'OperationDefinition',
    ) as OperationDefinitionNode[]).map(formatDefinition),
  };
};
